import React from 'react';
import { useStaticQuery, graphql } from 'gatsby';
import styled, { css } from 'styled-components';
import {
  minBreakpointQuery,
  maxBreakpointQuery,
  standardColours,
  brandColours,
  fontWeights,
  fontSize,
  zIndexLayers,
  standardTransition,
  visuallyHidden,
} from '../styles';
import { Container, Link } from './ui';

const StyledNavigation = styled.nav`
  background-color: ${standardColours.white};
  border-top: 1px solid ${standardColours.grey};
  border-bottom: 1px solid ${standardColours.grey};

  ${maxBreakpointQuery.mlarge`
    position: absolute;
    right: 0;
    left: 0;
    z-index: ${zIndexLayers.fourth};
    display: ${({ displayNav }) => (displayNav ? 'block' : 'none')};
    height: 100%;
  `}
`;

const StyledList = styled.ul`
  ${minBreakpointQuery.mlarge`
    display: flex;
    justify-content: space-between;
  `};
`;

// moved up for hover
const StyledLink = styled(Link)`
  position: relative;
  display: block;
  padding: 16px 20px;
  font-weight: ${fontWeights.medium};
  line-height: 1.4;
  transition: ${standardTransition('color')};

  ${minBreakpointQuery.small`
    padding-top: 20px;
    padding-bottom: 20px;
    ${fontSize(18)};
  `}

  ${minBreakpointQuery.mlarge`
    padding: 28px 14px;
    ${fontSize(15)};
    text-align: center;
    text-transform: uppercase;
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}

  span {
    ${maxBreakpointQuery.mlarge`
      ${visuallyHidden()};
    `}

    ${minBreakpointQuery.mlarge`
      position: absolute;
      top: 20%;
      right: 0;
      padding: 2px 8px;
      color: ${standardColours.white};
      ${fontSize(10)};
      text-transform: none;
      background-color: ${brandColours.dodectenary} ;
      transform: translateY(-50%);
    `}
  }
`;

// moved up for hover
const StyledSubNav = styled.div`
  position: absolute;
  left: -9999em;
  z-index: ${zIndexLayers.fourth};
  padding-top: 1px;
  width: 100%;
`;

const StyledItem = styled.li`
  ${maxBreakpointQuery.mlarge`
    border-top: 1px solid ${standardColours.grey};
  `}

  ${minBreakpointQuery.mlarge`
    display: inline-block;
  `}

  &:hover,
  &.current-page {
    ${StyledLink} {
      ${minBreakpointQuery.mlarge`
        color: ${brandColours.tertiary};
      `}
    }
  }

  &:hover {
    ${StyledSubNav} {
      ${minBreakpointQuery.mlarge`
        left: 0;
      `}
    }
  }

  ${({ hasChildren }) => {
    if (hasChildren) {
      return css`
        &:hover,
        &.current-page {
          ${StyledLink} {
            &:after {
              ${minBreakpointQuery.mlarge`
                border-top-color: ${brandColours.tertiary};
              `}
            }
          }
        }
      `;
    }
  }}
`;

const StyledSubNavInner = styled.div`
  background-color: ${standardColours.white};
  border: 1px solid ${standardColours.grey};
  border-top: 0;

  ${minBreakpointQuery.mlarge`
    padding: 30px 40px 40px;
  `}

  ${minBreakpointQuery.large`
    padding: 40px 50px 50px;
  `}

  ${minBreakpointQuery.xxlarge`
    padding: 50px 60px 60px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    padding: 60px 70px 70px;
  `}
`;

const StyledSubNavGroup = styled.div``;

const StyledSubNavHeading = styled.p`
  font-weight: ${fontWeights.bold};
  ${fontSize(14)};

  ${minBreakpointQuery.mlarge`
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}
`;

const StyledSubNavList = styled.ul``;

const StyledSubNavItem = styled.li`
  ${minBreakpointQuery.mlarge`
    margin-top: 16px;
  `}

  ${minBreakpointQuery.large`
    margin-top: 20px;
  `}

  ${minBreakpointQuery.xxlarge`
    margin-top: 24px;
  `}

  ${minBreakpointQuery.xxxxlarge`
    margin-top: 28px;
  `}
`;

const StyledSubNavLink = styled(Link)`
  ${fontSize(14)};
  transition: ${standardTransition('color')};

  ${minBreakpointQuery.mlarge`
    ${fontSize(15)};
  `}

  ${minBreakpointQuery.large`
    ${fontSize(16)};
  `}

  &:hover {
    color: ${brandColours.tertiary};
    text-decoration: underline;
  }
`;

const Navigation = ({ displayNav }) => {
  const {
    allDatoCmsNavigation: { nodes },
  } = useStaticQuery(graphql`
    query NavigationQuery {
      allDatoCmsNavigation(
        filter: { root: { eq: true } }
        sort: { fields: position, order: ASC }
      ) {
        nodes {
          link {
            text
            page {
              ...LinkFragment
            }
          }
          tag
          treeChildren {
            position
            link {
              text
              page {
                ...LinkFragment
              }
            }
          }
        }
      }
    }
  `);

  return (
    <StyledNavigation displayNav={displayNav}>
      <Container>
        <StyledList>
          {nodes.map((node, id) => {
            const hasChildren =
              node.treeChildren && node.treeChildren.length > 0;

            const { link, tag } = node;

            return (
              <StyledItem key={id} hasChildren={hasChildren}>
                <StyledLink
                  to={link.page}
                  hasChildren={hasChildren}
                  activeClassName="current-page"
                >
                  {link.text}
                  {tag && <span>{tag}</span>}
                </StyledLink>
                {hasChildren && (
                  <StyledSubNav>
                    <Container>
                      <StyledSubNavInner>
                        <StyledSubNavGroup>
                          <StyledSubNavHeading>
                            Shop by Category
                          </StyledSubNavHeading>
                          <StyledSubNavList>
                            {node.treeChildren
                              .sort((a, b) => a.position - b.position)
                              .map(({ link }, id) => (
                                <StyledSubNavItem key={id}>
                                  <StyledSubNavLink to={link.page}>
                                    {link.text}
                                  </StyledSubNavLink>
                                </StyledSubNavItem>
                              ))}
                          </StyledSubNavList>
                        </StyledSubNavGroup>
                      </StyledSubNavInner>
                    </Container>
                  </StyledSubNav>
                )}
              </StyledItem>
            );
          })}
        </StyledList>
      </Container>
    </StyledNavigation>
  );
};

export default Navigation;
